/// <reference types="@types/googlemaps" />
import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Inject,
    Injector,
    Input,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { ScrollbarComponent } from 'ngx-scrollbar';
import { Subject, Subscription } from 'rxjs';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { KeyCodes } from '../../../constants/keycodes.constants';
import { AutocompletePlace, ISelectOption } from '../../../models/common';
import { Utilities } from '../../../utilities/utilities.helper';
import { BaseFormControlComponent } from '../base-form-control.component';
import { LsqInputComponent } from '../input/lsq-input.component';
declare let google: any;
@Component({
    selector: 'lsq-autocomplete',
    templateUrl: './lsq-autocomplete.component.html',
    styleUrls: [
        '../input/lsq-input.component.scss',
        '../search/lsq-search.component.scss',
        './lsq-autocomplete.component.scss'
    ],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqAutocompleteComponent),
            multi: true
        }
    ]
})
export class LsqAutocompleteComponent extends BaseFormControlComponent<any>
    implements AfterContentInit {
    @ViewChild('inputElement', { static: true }) public input: LsqInputComponent;
    @ViewChild('dropdownMenuRef', { static: true }) public dropdownMenuRef: ElementRef;
    @ViewChildren('optionRefs') public optionRefs: QueryList<ElementRef>;
    @ViewChild('scrollBarRef', { static: true }) public scrollRef: ScrollbarComponent;
    @ViewChild('map', { static: true }) public mapElementRef: ElementRef;

    // tslint:disable:whitespace
    // TODO(story/15922): fix tslint whitespace issue.
    @Input() public name: string;
    @Input() public label: string;
    @Input() public group?: FormGroup;
    @Input() public control?: FormControl;
    @Input() public formControlName: string;
    @Input() public basicLabel? = false;
    @Input() public floatLabel? = false;
    @Input() public innerLabel? = true;
    @Input() public placeholder?: string;
    @Input() public parentClass?: string;
    @Input() public inputClass?: string;
    @Input() public labelClass?: string;
    @Input() public inputGroupClass? = '';
    @Input() public autoFocus?: boolean;
    @Input() public address? = true;
    @Input() public establishment? = false;

    @Output() public blur = new EventEmitter();
    @Output() public focus = new EventEmitter();
    @Output() public hasResults = new EventEmitter<boolean>();
    @Output() public selectResult = new EventEmitter<AutocompletePlace>();
    @Output() public textChanged = new EventEmitter();
    // tslint:enable:whitespace

    public color = Color;
    public icons = Icons;
    public isFocused = false;
    public isLoadingResults = false;
    public searchSubscription: Subscription;
    public showClearIcon = false;

    // The following google types did not compile into static build, so they were removed.
    // public addressResults: google.maps.places.QueryAutocompletePrediction[];
    // public autocompleteService: google.maps.places.AutocompleteService;
    // public establishmentResults: google.maps.places.AutocompletePrediction[];
    public addressResults: any;
    public autocompleteService: any;
    public establishmentResults: any;
    public options: ISelectOption[] = [];

    private autocompleteValueUpdated: Subject<string> = new Subject<string>();
    private _currentPosition;
    private _defaultSearchRadius: number;

    constructor(public injector: Injector, @Inject(SETTINGS) private _settings: ISettings) {
        super(injector);
        this._defaultSearchRadius = this._settings.googlePlacesSearchRadius;
    }

    public ngOnInit(): void {
        this.autocompleteService = new google.maps.places.AutocompleteService();
        this.getLocation().then(
            (response) => {
                this._currentPosition = response;
            },
            () => {
                // When fails, fail silently.
            }
        );
    }

    public ngAfterContentInit(): void {
        if (this.basicLabel) {
            this.floatLabel = false;
            this.innerLabel = false;
        }
        this.inputClass = this.inputClass ? `form-control ${this.inputClass}` : 'form-control';

        this.autocompleteValueUpdated.subscribe((value: string) => {
            if (
                this.input.inputElement &&
                this.input.inputElement.nativeElement &&
                this.input.inputElement.nativeElement.value &&
                this.input.inputElement.nativeElement.value.length > 1
            ) {
                this.isLoadingResults = true;
                this.getAutocompletePredictions(value);
            }
        });
    }

    public buildDropdownOptions(options: any[]): void {
        if (options && options.length) {
            this.options = options.map((option) => ({
                label: option.structured_formatting.main_text,
                subLabel: option.structured_formatting.secondary_text,
                value: option.place_id
            }));
        }
    }

    public getAutocompletePredictions(value: string): void {
        if (!value) {
            this.resetDropdown();
            return;
        }
        if (value.length > 0) {
            if (this.establishment) {
                this.getPlacePredictions(value);
            } else {
                this.getAddressPredictions(value);
            }
            Utilities.executeAfterRender(() => {
                if (this.scrollRef) {
                    const thumbRef = this.scrollRef.thumbYRef.nativeElement;
                    this.scrollRef.scrollYTo(0, 0);
                    // This fixes a visual bug with the plugin, it resets the scroll thumb.
                    thumbRef.style.transform = 'translate3d(0rem, 0rem, 0rem)';
                }
            });
        }
    }

    public getAddressPredictions(value: string): void {
        const request: google.maps.places.AutocompletionRequest = {
            input: value,
            location: new google.maps.LatLng(
                this._currentPosition.coords.latitude,
                this._currentPosition.coords.longitude
            ),
            radius: this._defaultSearchRadius,
            componentRestrictions: {
                // Experian search can only support USA.
                country: 'us'
            }
        };
        this.autocompleteService.getQueryPredictions(request, (response: any) =>
            this.buildDropdownOptions(response)
        );
    }

    public getLocation(): Promise<any> {
        const promise = new Promise<any>((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve(position);
                    },
                    () => {
                        reject(`Couldn't find your location`);
                    }
                );
            } else {
                reject(`Couldn't find your location`);
            }
        });
        return promise;
    }

    public getPlacePredictions(value: string): void {
        const request: google.maps.places.AutocompletionRequest = {
            input: value,
            location: new google.maps.LatLng(
                this._currentPosition.coords.latitude,
                this._currentPosition.coords.longitude
            ),
            radius: this._defaultSearchRadius,
            componentRestrictions: {
                // Experian search can only support USA.
                country: 'us'
            }
        };
        this.autocompleteService.getPlacePredictions(request, (response: any) =>
            this.buildDropdownOptions(response)
        );
    }

    public handleInputValueChanged(value: string): void {
        if (!this._currentPosition) {
            return;
        }

        if (value && value.length > 1) {
            this.autocompleteValueUpdated.next(value);
        } else {
            this.resetDropdown();
        }
    }

    public handleSelectItem(placeId: string): Promise<AutocompletePlace> {
        const promise = new Promise((resolve, reject) => {
            if (!placeId) {
                reject(placeId);
            }
            const place: AutocompletePlace = {};
            const service = new google.maps.places.PlacesService(
                new google.maps.Map(this.mapElementRef.nativeElement)
            );
            service.getDetails({ placeId: placeId }, (result: any) => {
                const streetNumber = this._mapAddressComponent(
                    'street_number',
                    result.address_components
                );
                const route = this._mapAddressComponent('route', result.address_components);
                place.address1 = streetNumber ? `${streetNumber} ${route}` : `${route}`;
                place.city = this._mapAddressComponent('locality', result.address_components);
                place.state = this._mapAddressComponent(
                    'administrative_area_level_1',
                    result.address_components
                );
                place.postalCode = this._mapAddressComponent(
                    'postal_code',
                    result.address_components
                );
                place.country = this._mapAddressComponent('country', result.address_components);
                place.phoneNumber = result.formatted_phone_number;
                place.singleLineAddress = result.formatted_address;
                place.companyName = result.name;
                place.website = result.website;
                resolve(place);
            });
        });
        return promise;
    }

    public handleViewportKeypress(event: KeyboardEvent, originalIndex?: number): void {
        if (event.which === KeyCodes.UpArrow || event.which === KeyCodes.DownArrow) {
            if (event.which === KeyCodes.UpArrow) {
                event.preventDefault();
                if (!this.highlightedOptionIndex) {
                    this.setHighlightedOption(
                        this.options.length - 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                } else {
                    this.setHighlightedOption(
                        this.highlightedOptionIndex - 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                }
            } else if (event.which === KeyCodes.DownArrow) {
                event.preventDefault();
                if (
                    this.highlightedOptionIndex == null ||
                    this.highlightedOptionIndex === this.options.length - 1
                ) {
                    this.setHighlightedOption(
                        0,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                } else {
                    this.setHighlightedOption(
                        this.highlightedOptionIndex + 1,
                        this.dropdownMenuRef,
                        this.optionRefs,
                        this.scrollRef
                    );
                }
            }
        } else if (event.which === KeyCodes.Enter) {
            event.preventDefault();
            if (
                this.options.length > 0 &&
                (this.highlightedOptionIndex || this.highlightedOptionIndex === 0)
            ) {
                this.selectItem(this.options[this.highlightedOptionIndex]);
            }
        } else if (event.which === KeyCodes.Escape) {
            this.resetDropdown();
        }
    }

    public resetDropdown(): void {
        this.options = [];
        this.highlightedOptionIndex = null;
    }

    public setFocus(isFocused: boolean): void {
        this.isFocused = isFocused;
        if (this.isFocused) {
            this.focus.emit();
        } else {
            this.blur.emit();
            this.resetDropdown();
        }
    }

    public selectItem(option: any): void {
        this.handleSelectItem(option.value).then((response) => {
            this.selectResult.emit(response);
            this.setFocus(false);
        });
    }

    private _mapAddressComponent(
        key: string,
        addressComponents: google.maps.GeocoderAddressComponent[]
    ): string {
        const result = addressComponents.find((field) => field.types.indexOf(key) >= 0);
        return result ? result.short_name : '';
    }

    // Jumper these ControlValueAccessor functions to the underlying lsq-input.
    public writeValue(model: string): void {
        this.input.writeValue(model);
    }
    public registerOnChange(fn: (_: string) => void): void {
        this.input.registerOnChange(fn);
    }
    public registerOnTouched(fn: () => void): void {
        this.input.registerOnTouched(fn);
    }
}
